import React from 'react';
import {
  Box, Card, CardContent, Typography, Avatar, Chip,
} from '@mui/material';
import {
  SportsSoccer as BallIcon,
  Star as StarIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  FitnessCenter as CoachIcon,
  LocalHospital as MedicalIcon,
  ManageAccounts as ManagerIcon,
} from '@mui/icons-material';
import { C, tRole } from './constants';
import { initials } from './utils';

// ─── Role helpers ────────────────────────────────────────────────────────────
export const roleIcon = (role) => ({
  player:             <BallIcon    sx={{ fontSize: 14 }} />,
  coach:              <CoachIcon   sx={{ fontSize: 14 }} />,
  manager:            <ManagerIcon sx={{ fontSize: 14 }} />,
  president:          <StarIcon    sx={{ fontSize: 14 }} />,
  directeur:          <StarIcon    sx={{ fontSize: 14 }} />,
  medical_staff:      <MedicalIcon sx={{ fontSize: 14 }} />,
  entityPlatformAdmin:<AdminIcon   sx={{ fontSize: 14 }} />,
}[role] || <PersonIcon sx={{ fontSize: 14 }} />);

export const roleColor = (role) => ({
  player:             C.accent,
  coach:              C.emerald,
  manager:            C.amber,
  president:          C.gold,
  directeur:          C.gold,
  medical_staff:      '#A78BFA',
  entityPlatformAdmin:C.rose,
}[role] || C.muted);

// ─── GlowCard ────────────────────────────────────────────────────────────────
export const GlowCard = ({ children, delay = 0, accent = C.accent, sx = {} }) => (
  <Card
    className="ed-card"
    elevation={0}
    sx={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: '16px',
      animation: `fadeUp 0.5s ease both`,
      animationDelay: `${delay}ms`,
      overflow: 'visible',
      position: 'relative',
      '&::before': {
        content: '""', position: 'absolute', inset: 0, borderRadius: '16px',
        background: `linear-gradient(135deg, ${accent}08, transparent 60%)`,
        pointerEvents: 'none',
      },
      ...sx,
    }}
  >
    {children}
  </Card>
);

// ─── StatBar ─────────────────────────────────────────────────────────────────
export const StatBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <Box sx={{
      position: 'relative', height: 6, borderRadius: 99,
      background: C.border, overflow: 'hidden', mt: 0.5,
    }}>
      <Box sx={{
        position: 'absolute', left: 0, top: 0, height: '100%',
        width: `${pct}%`, borderRadius: 99,
        background: `linear-gradient(90deg, ${color}, ${color}99)`,
        transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </Box>
  );
};

// ─── BigStat ─────────────────────────────────────────────────────────────────
export const BigStat = ({ value, label, icon, color, delay }) => (
  <GlowCard delay={delay} accent={color} sx={{ height: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{
            fontFamily: 'Syne, sans-serif', fontWeight: 800,
            fontSize: '2.6rem', lineHeight: 1, color: C.text,
            animation: `countUp 0.5s ease both`,
            animationDelay: `${delay + 100}ms`,
          }}>
            {value}
          </Typography>
          <Typography sx={{
            fontFamily: 'DM Sans', color: C.muted, mt: 0.5,
            fontSize: '0.82rem', letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          background: `${color}18`, border: `1px solid ${color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color, animation: `pulse 2.5s ease infinite`,
          animationDelay: `${delay}ms`,
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </GlowCard>
);

// ─── MemberRow ───────────────────────────────────────────────────────────────
export const MemberRow = ({ member, index }) => {
  const role     = member.teamMemberInfo?.role || 'player';
  const color    = roleColor(role);
  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email || 'Inconnu';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, py: 1.2,
      borderBottom: `1px solid ${C.border}`,
      animation: `fadeUp 0.4s ease both`,
      animationDelay: `${index * 50 + 300}ms`,
      '&:last-child': { borderBottom: 'none' },
      transition: 'background 0.2s', borderRadius: '8px', px: 1,
      '&:hover': { background: `${C.border}50` },
    }}>
      <Avatar sx={{
        width: 36, height: 36,
        background: `linear-gradient(135deg, ${color}40, ${color}20)`,
        border: `1px solid ${color}50`,
        color, fontFamily: 'Syne', fontWeight: 700, fontSize: '0.75rem',
      }}>
        {initials(fullName)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: 'DM Sans', fontWeight: 500, color: C.text, fontSize: '0.88rem' }}>
          {fullName}
        </Typography>
        {member.teamMemberInfo?.positionOnField && (
          <Typography sx={{ fontFamily: 'DM Sans', color: C.muted, fontSize: '0.72rem' }}>
            {member.teamMemberInfo.positionOnField}
          </Typography>
        )}
      </Box>

      <Chip
        icon={roleIcon(role)}
        label={tRole(role)}
        size="small"
        sx={{
          background: `${color}15`, border: `1px solid ${color}30`, color,
          fontFamily: 'DM Sans', fontSize: '0.7rem', fontWeight: 500,
          '& .MuiChip-icon': { color },
        }}
      />

      {member.teamMemberInfo?.jerseyNumber && (
        <Typography sx={{
          fontFamily: 'Syne', fontWeight: 700, color: C.dim,
          fontSize: '0.8rem', minWidth: 24, textAlign: 'right',
        }}>
          #{member.teamMemberInfo.jerseyNumber}
        </Typography>
      )}
    </Box>
  );
};
